import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Bed, Pill, AlertTriangle, Activity, TrendingUp, Heart, Calendar, Package, ClipboardList, CalendarCheck, Stethoscope, ShieldAlert } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Button } from '@/components/ui/button';
import { LIVELLO_STILE, LivelloBadge } from '@/components/alertStyles';

const PIE_COLORS = ['#076633', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [scorteAlert, setScorteAlert] = useState(0);
  const [paiScadenza, setPaiScadenza] = useState([]);
  const [appDomani, setAppDomani] = useState([]);
  const [alertClinici, setAlertClinici] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/report/kpi'),
      api.get('/scorte/in-esaurimento'),
      api.get('/pai/in-scadenza', { params: { giorni: 180 } }),
      api.get('/appuntamenti/domani'),
      api.get('/alert/riepilogo', { params: { solo_attivi: true } })
    ]).then(([kpiRes, scorteRes, paiRes, appRes, alertRes]) => {
      setKpi(kpiRes.data);
      setScorteAlert(scorteRes.data.length);
      setPaiScadenza(paiRes.data);
      setAppDomani(appRes.data);
      setAlertClinici(alertRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;

  const kpiCards = [
    { label: 'Ospiti Presenti', value: kpi?.totale_ospiti || 0, sub: `su ${kpi?.totale_posti || 80} posti`, icon: Users, color: 'bg-blue-50 text-brand' },
    { label: 'Occupazione', value: `${kpi?.occupazione_percentuale || 0}%`, sub: 'posti letto', icon: Bed, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Terapie Attive', value: kpi?.terapie_attive || 0, sub: `${kpi?.somministrazioni_mancate || 0} mancate oggi`, icon: Pill, color: 'bg-amber-50 text-amber-600' },
    { label: 'Cadute Mese', value: kpi?.cadute_mese || 0, sub: `${kpi?.lesioni_mese || 0} lesioni`, icon: AlertTriangle, color: 'bg-red-50 text-red-600' },
  ];

  const paiScaduti = paiScadenza.filter(p => p.scaduto);
  const paiInScadenza = paiScadenza.filter(p => !p.scaduto);

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Benvenuto, {user?.full_name}</p>
        </div>
        <Badge variant="outline" className="text-xs font-medium">
          <Calendar className="w-3 h-3 mr-1" />
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        {kpiCards.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="card-hover animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{k.label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1 font-[Cinzel]">{k.value}</p>
                    <p className="text-xs text-slate-400 mt-1">{k.sub}</p>
                  </div>
                  <div className={`p-2.5 rounded-lg ${k.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-brand" />Trend Eventi Avversi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kpi?.eventi_trend || []} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mese" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="eventi" fill="#076633" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '0.25s' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" />Eventi per Tipologia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={kpi?.eventi_per_tipo || []} dataKey="conteggio" nameKey="tipo" cx="50%" cy="50%" outerRadius={90} label={({ tipo, conteggio }) => conteggio > 0 ? `${tipo}: ${conteggio}` : ''} labelLine={false} style={{ fontSize: 11 }}>
                    {(kpi?.eventi_per_tipo || []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert widgets row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {appDomani.length > 0 && (
          <Card className="card-hover border-blue-200 bg-blue-50/30 cursor-pointer" onClick={() => navigate('/appuntamenti')} data-testid="appuntamenti-domani-widget">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-lg bg-blue-100"><CalendarCheck className="w-5 h-5 text-brand" /></div>
              <div>
                <p className="text-xs text-brand font-medium">Visite Domani</p>
                <p className="text-xl font-bold text-blue-700">{appDomani.length}</p>
              </div>
            </CardContent>
          </Card>
        )}
        {scorteAlert > 0 && (
          <Card className="card-hover border-red-200 bg-red-50/30 cursor-pointer" onClick={() => navigate('/ordini-farmaci')} data-testid="scorte-alert-widget">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-lg bg-red-100"><Package className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-xs text-red-600 font-medium">Farmaci in Esaurimento</p>
                <p className="text-xl font-bold text-red-700">{scorteAlert}</p>
              </div>
            </CardContent>
          </Card>
        )}
        {paiScadenza.length > 0 && (
          <Card className={`card-hover cursor-pointer ${paiScaduti.length > 0 ? 'border-orange-200 bg-orange-50/30' : 'border-amber-200 bg-amber-50/30'}`} data-testid="pai-scadenza-widget">
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`p-2 rounded-lg ${paiScaduti.length > 0 ? 'bg-orange-100' : 'bg-amber-100'}`}>
                <ClipboardList className={`w-5 h-5 ${paiScaduti.length > 0 ? 'text-orange-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className={`text-xs font-medium ${paiScaduti.length > 0 ? 'text-orange-600' : 'text-amber-600'}`}>PAI da Rivalutare</p>
                <p className={`text-xl font-bold ${paiScaduti.length > 0 ? 'text-orange-700' : 'text-amber-700'}`}>
                  {paiScadenza.length}
                  {paiScaduti.length > 0 && <span className="text-xs font-medium ml-1 text-red-600">({paiScaduti.length} scaduti)</span>}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-blue-50"><Activity className="w-5 h-5 text-brand" /></div>
            <div>
              <p className="text-xs text-slate-500">Somministrazioni Oggi</p>
              <p className="text-xl font-bold text-slate-900">{kpi?.somministrazioni_oggi || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-emerald-50"><Users className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Ospiti Ricoverati</p>
              <p className="text-xl font-bold text-slate-900">{kpi?.ospiti_ricoverati || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert clinici (motore a regole) */}
      {alertClinici && alertClinici.ospiti.length > 0 && (
        <Card data-testid="alert-clinici-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-600" />Sorveglianza Clinica
              <Badge className="bg-red-100 text-red-700 text-[10px]">{alertClinici.ospiti_rossi} critici</Badge>
              <Badge className="bg-amber-100 text-amber-700 text-[10px]">{alertClinici.ospiti_gialli} attenzione</Badge>
              <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => navigate('/scale-alert')} data-testid="vai-scale-alert">
                Apri pannello completo
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertClinici.ospiti.slice(0, 6).map(o => {
                const stile = LIVELLO_STILE[o.livello];
                return (
                  <div key={o.ospite_id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer ${stile.card}`}
                    onClick={() => navigate(`/ospiti/${o.ospite_id}`)} data-testid={`dash-alert-${o.ospite_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${stile.dot}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{o.ospite_nome}</p>
                        <p className="text-[11px] text-slate-400 truncate">{o.alert.map(a => a.titolo).slice(0, 3).join(' · ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {o.news2 !== null && o.news2 !== undefined && <Badge variant="outline" className="text-[10px]">NEWS2 {o.news2}</Badge>}
                      <LivelloBadge livello={o.livello} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* PAI in scadenza detail list */}
      {paiScadenza.length > 0 && (
        <Card data-testid="pai-scadenza-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-amber-600" />PAI in Scadenza / Scaduti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {paiScadenza.map((p, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${p.scaduto ? 'bg-red-50 border-red-200' : 'bg-amber-50/50 border-amber-200'}`}
                  onClick={() => navigate(`/ospiti/${p.ospite_id}`)} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${p.scaduto ? 'bg-red-500' : 'bg-amber-500'}`} />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{p.ospite_nome}</p>
                      <p className="text-[10px] text-slate-400">Camera {p.stanza} - {p.area || 'N/D'}</p>
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${p.scaduto ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {p.scaduto ? 'Scaduto' : 'In scadenza'}: {new Date(p.data_prossima_rivalutazione).toLocaleDateString('it-IT')}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appuntamenti domani detail */}
      {appDomani.length > 0 && (
        <Card data-testid="appuntamenti-domani-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-brand" />Visite Programmate per Domani
              <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-2">{appDomani.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {appDomani.map((a, i) => {
                const dt = new Date(a.data_ora);
                const tipiLabels = { prelievo_ematico: 'Prelievo Ematico', visita_cardiologica: 'Visita Cardiologica', visita_ortopedica: 'Visita Ortopedica', visita_neurologica: 'Visita Neurologica', controllo_mmg: 'Controllo MMG', esame_diagnostico: 'Esame Diagnostico', ecografia: 'Ecografia', radiografia: 'Radiografia', tac: 'TAC', fisioterapia: 'Fisioterapia' };
                return (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-blue-50/50 border-blue-200 cursor-pointer"
                    onClick={() => navigate('/appuntamenti')}>
                    <div className="flex items-center gap-3">
                      <div className="text-center min-w-[40px]">
                        <p className="text-sm font-bold text-blue-700">{dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{a.ospite_nome}</p>
                        <p className="text-[10px] text-slate-400">Camera {a.stanza || '-'}/{a.letto || '-'} {a.area ? `(${a.area})` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]"><Stethoscope className="w-3 h-3 mr-0.5" />{tipiLabels[a.tipo] || a.tipo}</Badge>
                      {a.specialista && <span className="text-xs text-slate-500">{a.specialista}</span>}
                      {a.luogo_tipo === 'esterno' && <Badge className="text-[9px] bg-purple-100 text-purple-600">EST</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
